'use client';

import React, { useState, Dispatch, SetStateAction } from 'react';
import { useForm } from 'react-hook-form';
import { InputMask } from '@react-input/mask';
import Link from 'next/link';
import { GenerateErrorMessage, InputField, TextAreaField } from './fields';
import { Spinner } from '@/components/spinner';
import { Tooltip } from '@/components/tooltip';
import { analytics } from '@/utils/analytics';
import { submitFormByType } from '@/services/v2/forms';
import { getPatterns, getMinValues, getMaxValues } from '@/utils/formUtils';

const ToolTipText = () => (
  <div className="mx-auto w-mobile p-4">
    <h4 className="my-5 text-center">
      Finding your home&rsquo;s serial&nbsp;number
    </h4>
    <div className="text-[14px] text-grey-1 *:mb-2.5">
      <p>
        Your home&rsquo;s serial number can be found on the home&rsquo;s data
        plate which is located under the kitchen sink on the cabinet door or in
        the primary bedroom closet. The serial number should look similar to:
        ABC123456ABAB. You can also find the serial number on your paperwork
        from when you purchased your home.
      </p>
      <p>
        The data plate is a laminated sheet of paper with information about your
        home including the wind zone to which it was built and the home building
        facility that built your home.
      </p>
    </div>
  </div>
);

const GenerateForm = ({
  setFormStatus,
  formRoute,
}: {
  setFormStatus: Dispatch<SetStateAction<'success' | 'error' | null>>;
  formRoute: 'homeowner' | 'contactus';
}) => {
  const [isFormSending, setIsFormSending] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      comments: '',
      preferredContactMethod: 'email',
      serialNumber: '',
      purchaseLocation: '',
      yearPurchased: '',
      consent: false,
    },
  });

  const onSubmit = async () => {
    const formValues = getValues();
    setIsFormSending(true);
    try {
      const formResponse = await submitFormByType(formRoute, formValues);
      if (formResponse) {
        setFormStatus('success');
        analytics?.track('Generic Contact Form Submitted', {
          formId:
            formRoute === 'homeowner' ? 'homeowner-contact-us' : 'contact-us',
          firstName: formValues?.firstName,
          lastName: formValues?.lastName,
          email: formValues?.email,
          phone: formValues?.phone,
        });

        if (typeof window !== 'undefined')
          window.scrollTo({ top: 0, behavior: 'smooth' });
      } else throw new Error('Form submission failed.');
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormStatus('error');
      setIsFormSending(false);
    }
  };

  const renderInputField = (
    name: string,
    placeholder: string,
    type: string = 'text',
    validation = {}
  ) => (
    <InputField
      name={name}
      placeholder={placeholder}
      type={type}
      register={register}
      errors={errors}
      validation={validation}
    />
  );

  return (
    <div className="mx-auto my-5 w-mobile pt-[30px]">
      <form
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="on"
        id="contact-us-form"
        name="contact-us-form"
      >
        <div className="grid grid-cols-1 gap-4 text-grey-1 lg:grid-cols-2">
          {renderInputField('firstName', 'First Name*', 'text', {
            required: 'First name is required.',
            pattern: getPatterns('firstName'),
            minLength: getMinValues('firstName'),
            maxLength: getMaxValues('firstName'),
          })}
          {renderInputField('lastName', 'Last Name*', 'text', {
            required: 'Last name is required.',
            pattern: getPatterns('lastName'),
            minLength: getMinValues('lastName'),
            maxLength: getMaxValues('lastName'),
          })}
          <div className="relative">
            <InputMask
              {...register('phone', {
                required: 'Phone number is required',
                pattern: getPatterns('phone'),
                minLength: getMinValues('phone'),
                maxLength: getMaxValues('phone'),
              })}
              mask="___-___-____"
              placeholder="Phone*"
              replacement="_"
              type="text"
              className="my-2 w-full border-b border-b-grey-1 p-2 font-acumin text-[16px] placeholder:text-grey-1"
            />
            {errors && GenerateErrorMessage(errors, 'phone')}
          </div>
          {renderInputField('email', 'Email*', 'email', {
            required: 'E-mail address is required.',
            pattern: getPatterns('email'),
            minLength: getMinValues('email'),
            maxLength: getMaxValues('email'),
          })}
        </div>
        {formRoute === 'homeowner' && (
          <div className="my-8">
            <div className="relative my-4 w-full sm:inline-flex">
              <p className="text-[16px] text-grey-1">
                Preferred Contact Method*
              </p>
              <div className="inline-flex">
                <input
                  {...register('preferredContactMethod', {
                    required: 'Contact method is required.',
                  })}
                  value="Phone"
                  id="phone"
                  type="radio"
                  className="py-4 sm:ml-8"
                />
                <label htmlFor="phone" className="ml-2 mr-4 text-grey-1">
                  Phone
                </label>
                <input
                  {...register('preferredContactMethod', {
                    required: 'Contact method is required.',
                  })}
                  value="Email"
                  type="radio"
                />
                <label htmlFor="phone" className="ml-2 text-grey-1">
                  Email
                </label>
              </div>
              {errors && GenerateErrorMessage(errors, 'contactMethod')}
            </div>
            {renderInputField('serialNumber', 'Serial Number*', 'text', {
              required: 'Serial number is required.',
            })}
            <Tooltip text="How to find your home's serial number">
              {ToolTipText()}
            </Tooltip>
            <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-10">
              {renderInputField(
                'purchaseLocation',
                'Purchase Location',
                'text',
                {
                  maxLength: getMaxValues('city'),
                }
              )}
              {renderInputField('yearPurchased', 'Purchase Year', 'text', {
                pattern: getPatterns('yearPurchased'),
                maxLength: 4,
              })}
            </div>
          </div>
        )}
        <TextAreaField
          name="comments"
          placeholder="Comments (Max 500 characters)"
          register={register}
          errors={errors}
          validation={{
            pattern: getPatterns('comments'),
            maxLength: getMaxValues('comments'),
          }}
        />
        {formRoute === 'contactus' && (
          <div className="relative mt-4 flex flex-row items-center gap-2.5 md:gap-7">
            <input
              {...register('consent', {
                required:
                  'Please check the box to agree to the Terms & Conditions.',
              })}
              type="checkbox"
              id="consent"
              name="consent"
              placeholder="checked"
              defaultChecked={false}
              style={{ width: '75px', height: '20px' }}
            />
            <p className="mr-2.5">
              By checking this box, I agree that Clayton may contact me by
              telephone, provide me with marketing communications about Clayton
              products and services, and share my contact information as
              described in our{' '}
              <Link
                href="https://www.claytonhomes.com/account-terms-conditions/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms &amp; Conditions
              </Link>{' '}
              and{' '}
              <Link
                href="https://www.claytonhomes.com/privacy/"
                target="_blank"
                rel="noopenner noreferrer"
              >
                Privacy Policy
              </Link>
              .
            </p>
            {errors && GenerateErrorMessage(errors, 'consent')}
          </div>
        )}
        <button
          type="submit"
          id="submit-button"
          disabled={isFormSending}
          className="mx-auto mt-12 flex w-full cursor-pointer justify-center rounded-sm bg-clayton-blue px-[30px] py-[13px] font-source-sans text-[16px] font-semibold capitalize text-white disabled:pointer-events-none disabled:bg-gray-400 disabled:text-black-standard disabled:opacity-70"
        >
          {isFormSending ? (
            <span className="inline-flex gap-4">
              Sending <Spinner size="sm" color="dark" className="mt-1" />
            </span>
          ) : (
            'Submit'
          )}
        </button>
      </form>
    </div>
  );
};

export const Form = () => {
  const [formStatus, setFormStatus] = useState<'success' | 'error' | null>(
    null
  );
  const [formType, setFormType] = useState<'homeowner' | 'contactus' | null>(
    null
  );

  const handleButtonClick = (type: 'homeowner' | 'contactus') => {
    setFormType(type);
  };

  return (
    <div className="max-w-[575px]">
      {formStatus === 'success' && (
        <div className="mx-auto w-mobile text-center">
          <h5 className="my-[25px] text-[16px]">Message sent!</h5>
          <p className="text-[16px] text-gray-500">
            A team member will reach out to you as soon as possible.
          </p>
        </div>
      )}
      {formStatus === 'error' && (
        <div className="mx-auto w-mobile pt-4 text-[30px] font-semibold text-red-500">
          Error. Sorry. We could not process your request, please try again
          later.
        </div>
      )}
      {!formStatus && (
        <>
          <p className="mb-5 text-center">How can we help?</p>
          <div className="mx-auto flex w-2/3 flex-col gap-2 text-hbg-blue lg:w-full lg:flex-row lg:justify-center lg:gap-6">
            <button
              className="border border-gray-200 p-4"
              onClick={() => handleButtonClick('homeowner')}
            >
              I own a Clayton home
            </button>
            <button
              className="border border-gray-200 p-4"
              onClick={() => handleButtonClick('contactus')}
            >
              I have a general inquiry
            </button>
          </div>
          {formType && (
            <GenerateForm setFormStatus={setFormStatus} formRoute={formType} />
          )}
        </>
      )}
    </div>
  );
};
